import { CartItem, Product } from '../types';

export type PODProductType = 'calendar' | 'business_card' | 'customize_item';

export interface FieldDef {
  key: string;
  label: string;
}

// UI toggle fields inside printOnDemandFields that aren't real copyable values
// (mirrors BusinessCardForm.META_FIELDS / CustomizeItemForm equivalents)
export const META_FIELD_KEYS = ['is_saved_address', 'address_group', 'slight_bar'];

// Visual settings living alongside field values in a calendar LocationData — never copyable
export const CALENDAR_NON_FIELD_KEYS = ['textColor', 'formScale', 'formOpacity', 'formPosition'];

// Mirrors CalendarCustomizer.defaultLocationState's visual defaults — used as the base when a
// calendar location doesn't exist yet on the destination, so a fresh copy doesn't leave these unset
export const CALENDAR_LOCATION_DEFAULTS: Record<string, string | number> = {
  textColor: '#000000',
  formScale: 0.5,
  formOpacity: 0.2,
};

// Mirrors LocationForm.DEFAULT_FIELD_KEYS fallback for calendar products with no printOnDemandFields
export const DEFAULT_CALENDAR_FIELD_KEYS = [
  'agency_combine', 'additional_agents', 'address', 'city', 'state',
  'zip_code', 'phone', 'cell_phone', 'fax', 'other_info',
];

export const CALENDAR_SLOTS = ['primary', 'secondary', 'tertiary'] as const;
export type CalendarSlot = typeof CALENDAR_SLOTS[number];

export function resolveFieldDefs(product: Product, type: PODProductType): FieldDef[] {
  const fields = (product.printOnDemandFields ?? []).filter(f => !META_FIELD_KEYS.includes(f.key));
  if (fields.length > 0) return fields;
  if (type === 'calendar') return DEFAULT_CALENDAR_FIELD_KEYS.map(key => ({ key, label: key }));
  return fields;
}

export function getStableCartItemId(item: CartItem): string {
  return item.id || `${item.productId}-${item.variationId}`;
}

export function isPrintOnDemandCartItem(item: CartItem, type: PODProductType): boolean {
  return !!item.product.isPrintDetail && item.product.printDetailType === type;
}

export function getLocationFieldValues(
  location: Record<string, unknown> | undefined,
  fieldKeys: string[],
): Record<string, string> {
  const values: Record<string, string> = {};
  if (!location) return values;
  for (const key of fieldKeys) {
    if (CALENDAR_NON_FIELD_KEYS.includes(key)) continue;
    const raw = location[key];
    values[key] = typeof raw === 'string' ? raw : raw != null ? String(raw) : '';
  }
  return values;
}

function getItemLocations(item: CartItem, type: PODProductType): Record<string, unknown>[] {
  if (type === 'calendar') {
    const locations = item.calendarCustomization?.locations;
    if (!locations) return [];
    return CALENDAR_SLOTS
      .map(slot => locations[slot])
      .filter((location): location is Record<string, unknown> => !!location);
  }
  const data = type === 'business_card' ? item.businessCardCustomization : item.customizeItemCustomization;
  return data ? [data] : [];
}

export function hasAnyPersonalizationValue(item: CartItem, type: PODProductType): boolean {
  const fieldKeys = resolveFieldDefs(item.product, type).map(f => f.key);
  if (fieldKeys.length === 0) return false;
  return getItemLocations(item, type).some(location =>
    Object.values(getLocationFieldValues(location, fieldKeys)).some(value => value.trim() !== '')
  );
}

export function getEligibleCopySources(
  cart: CartItem[],
  targetType: PODProductType,
  excludeCartItemId?: string,
): CartItem[] {
  return cart.filter(item =>
    isPrintOnDemandCartItem(item, targetType) &&
    getStableCartItemId(item) !== excludeCartItemId &&
    hasAnyPersonalizationValue(item, targetType)
  );
}

export function formatCopySourceLabel(item: CartItem, type: PODProductType, ownFieldDefs: FieldDef[]): string {
  const variationLabel = [item.variation.color, item.variation.size].filter(Boolean).join(' / ');
  const parts = [item.product.name];
  if (variationLabel) parts.push(`– ${variationLabel}`);
  parts.push(`(Qty ${item.quantity})`);

  const fieldKeys = ownFieldDefs.map(f => f.key);
  const locations = getItemLocations(item, type);
  let preview: string | undefined;
  for (const location of locations) {
    const values = getLocationFieldValues(location, fieldKeys);
    const firstValue = Object.values(values).find(value => value.trim() !== '');
    if (firstValue) {
      preview = firstValue.length > 24 ? `${firstValue.slice(0, 24)}…` : firstValue;
      break;
    }
  }

  const label = parts.join(' ');
  return preview ? `${label} — "${preview}"` : label;
}

export interface FieldMapping {
  sourceKey: string;
  destKey: string;
  label: string;
}

export function buildFieldMapping(sourceFields: FieldDef[], destFields: FieldDef[]): FieldMapping[] {
  const sourceKeys = new Set(sourceFields.map(f => f.key));
  return destFields
    .filter(destField => sourceKeys.has(destField.key))
    .map(destField => ({ sourceKey: destField.key, destKey: destField.key, label: destField.label }));
}

export interface OverwrittenField {
  destKey: string;
  label: string;
  oldValue: string;
  newValue: string;
}

export interface CopyPreview {
  toWrite: Record<string, string>;
  overwritten: OverwrittenField[];
}

export function computeCopyPreview(
  sourceValues: Record<string, string>,
  destValues: Record<string, string>,
  mapping: FieldMapping[],
): CopyPreview {
  const toWrite: Record<string, string> = {};
  const overwritten: OverwrittenField[] = [];

  for (const { sourceKey, destKey, label } of mapping) {
    const newValue = sourceValues[sourceKey];
    if (!newValue || newValue.trim() === '') continue;

    const oldValue = destValues[destKey] ?? '';
    if (oldValue.trim() !== '' && oldValue !== newValue) {
      overwritten.push({ destKey, label, oldValue, newValue });
    }
    toWrite[destKey] = newValue;
  }

  return { toWrite, overwritten };
}

export function applyCopyToSlot<T extends Record<string, unknown>>(
  destSlot: T | undefined,
  toWrite: Record<string, string>,
): T {
  return { ...(destSlot ?? {}), ...toWrite } as T;
}
